import React from 'react';

import { ReportContext } from './hooks/use-context';
import type { EventReportData, QuestionData } from './types';

// Helper function to format date from API response
function formatDate(dateString: string): string {
  if (!dateString) return '';
  const [datePart] = dateString.split(' ');
  return datePart || '';
}

// Helper function to format time from API response
function formatTime(dateString: string): string {
  if (!dateString) return '';
  const parts = dateString.split(' ');
  if (parts.length < 2) return '';
  const [hours, minutes] = parts[1].split(':');
  return `${hours}:${minutes}`;
}

// Helper function to get answer content by question NAME
function getAnswerContentByName(
  questions: QuestionData[],
  questionName: string,
): string {
  const question = questions.find((q) => q.name === questionName);
  if (!question || !question.answers || question.answers.length === 0)
    return '';

  const answer = question.answers[0];
  return answer.option || answer.content || '';
}

// Helper to get the selected option (useful for radio/radio_combo)
function getAnswerOptionByName(
  questions: QuestionData[],
  questionName: string,
): string {
  const question = questions.find((q) => q.name === questionName);
  if (!question || !question.answers || question.answers.length === 0)
    return '';
  const answer = question.answers[0];
  return answer.option || '';
}

// Map option to display mark
function optionToMark(option: string): string {
  const val = (option || '').toLowerCase();
  if (!val) return '';
  if (val.startsWith('conforme')) return 'C';
  if (val.includes('não')) return 'NC';
  return '';
}

// Helper to extract month name (pt-BR) from date string like 'DD/MM/YYYY HH:mm'
function getMonthNameFromDateString(dateString: string): string {
  if (!dateString) return '';
  const [datePart] = dateString.split(' ');
  const parts = (datePart || '').split('/');
  if (parts.length < 2) return '';
  const monthNum = Number(parts[1]);
  const MONTHS = [
    'janeiro',
    'fevereiro',
    'março',
    'abril',
    'maio',
    'junho',
    'julho',
    'agosto',
    'setembro',
    'outubro',
    'novembro',
    'dezembro',
  ];
  if (!Number.isFinite(monthNum) || monthNum < 1 || monthNum > 12) return '';
  return MONTHS[monthNum - 1];
}

export function IzuReportHygiene(props: { reportData: EventReportData }) {
  const { reportData } = props;

  // Extract questions from the first section
  const questions =
    reportData?.body?.items?.[0]?.sections?.[0]?.questions || [];
  const header = reportData?.header;
  const firstItem = reportData?.body?.items?.[0];

  // Question NAMES based on API example for Higienização
  const QUESTION_NAMES = {
    // Diária
    piso: 'Piso',
    pia: 'Pia',
    sifaoPia: 'Sifão de Pia',
    mesa: 'Mesa',
    bebedouros: 'Bebedouros',
    bancos: 'Bancos',
    lixeira: 'Lixeira',
    sofa: 'Sofá',
    retirarLixo: 'Retirar Lixo',
    // Semanal / conforme necessidade
    microOndas: 'Micro-ondas',
    geladeira: 'Geladeira',
    armarios: 'Armários',
    teto: 'Teto',
    // Outros
    naoConformidade: 'Descreva a Não Conformidade',
    responsavelNome: 'Nome do Colaborador Responssável',
    assinatura: 'Assinatura do Colaborador Responsável',
  } as const;

  const eventDateTime =
    header?.event_finished_at ||
    header?.event_date_time ||
    header?.event_created_at ||
    '';
  const date = formatDate(eventDateTime);
  // const time = formatTime(eventDateTime);
  const monthName = getMonthNameFromDateString(eventDateTime);

  // Day of month (1..31) to place the mark
  const dayOfMonth = (() => {
    const [d, m, y] = date.split('/');
    const n = Number(d);
    return Number.isFinite(n) && n >= 1 && n <= 31 ? n : undefined;
  })();

  // Build marks per item based on option
  const marks = {
    piso: optionToMark(getAnswerOptionByName(questions, QUESTION_NAMES.piso)),
    pia: optionToMark(getAnswerOptionByName(questions, QUESTION_NAMES.pia)),
    sifaoPia: optionToMark(
      getAnswerOptionByName(questions, QUESTION_NAMES.sifaoPia),
    ),
    mesa: optionToMark(getAnswerOptionByName(questions, QUESTION_NAMES.mesa)),
    bebedouros: optionToMark(
      getAnswerOptionByName(questions, QUESTION_NAMES.bebedouros),
    ),
    bancos: optionToMark(
      getAnswerOptionByName(questions, QUESTION_NAMES.bancos),
    ),
    lixeira: optionToMark(
      getAnswerOptionByName(questions, QUESTION_NAMES.lixeira),
    ),
    sofa: optionToMark(getAnswerOptionByName(questions, QUESTION_NAMES.sofa)),
    retirarLixo: optionToMark(
      getAnswerOptionByName(questions, QUESTION_NAMES.retirarLixo),
    ),
    microOndas: optionToMark(
      getAnswerOptionByName(questions, QUESTION_NAMES.microOndas),
    ),
    geladeira: optionToMark(
      getAnswerOptionByName(questions, QUESTION_NAMES.geladeira),
    ),
    armarios: optionToMark(
      getAnswerOptionByName(questions, QUESTION_NAMES.armarios),
    ),
    teto: optionToMark(getAnswerOptionByName(questions, QUESTION_NAMES.teto)),
  } as const;

  const responsavelNome = getAnswerContentByName(
    questions,
    QUESTION_NAMES.responsavelNome,
  );

  // Footer data from API
  const elaboradoPor = firstItem?.formulary_last_author || '-';
  const dataElaboracao = firstItem?.formulary_updated_at || '-';
  const aprovadoPor = header?.event_validated_author || '-';
  const dataRevisao = header?.event_validated_at || '-';

  // Utility to render a row with 31 days and place mark on event day
  const renderDayCells = (mark: string) => {
    const cells = [] as React.ReactNode[];
    for (let day = 1; day <= 31; day += 1) {
      const display = dayOfMonth === day ? mark : '';
      cells.push(
        <td
          key={`d_${day}`}
          style={{
            border: '1px solid #000',
            padding: '4px',
            textAlign: 'center',
            height: '18px',
          }}
        >
          {display}
        </td>,
      );
    }
    return cells;
  };

  return (
    <ReportContext value={reportData}>
      <div
        style={{
          width: '297mm',
          minHeight: '210mm',
          backgroundColor: '#ffffff',
          fontFamily: 'Arial, sans-serif',
          padding: '20mm',
          margin: '0 auto',
          boxSizing: 'border-box',
        }}
      >
        {/* Header Section */}
        <div style={{ marginBottom: '10px' }}>
          <table
            style={{
              width: '100%',
              borderCollapse: 'collapse',
              border: '2px solid #000',
            }}
          >
            <tbody>
              <tr>
                <td
                  style={{
                    width: '20%',
                    padding: '10px',
                    border: '1px solid #000',
                    textAlign: 'center',
                  }}
                >
                  {header?.logo && (
                    <img
                      src={header.logo}
                      alt="Logo"
                      style={{ maxWidth: '100px', height: 'auto' }}
                    />
                  )}
                </td>
                <td
                  style={{
                    width: '50%',
                    padding: '10px',
                    border: '1px solid #000',
                    textAlign: 'center',
                    fontWeight: 'bold',
                    fontSize: '16px',
                  }}
                >
                  REGISTRO DE HIGIENIZAÇÃO
                </td>
                <td
                  style={{
                    width: '30%',
                    padding: '10px',
                    border: '1px solid #000',
                  }}
                >
                  <div style={{ marginBottom: '5px' }}>PCQ1-01</div>
                  <div>Página: 1 de 1</div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        {/* Month and Area Section */}
        <div style={{ marginBottom: '8px', fontSize: '14px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <div>Mês: {monthName}</div>
            {header?.local?.name && (
              <div>Área de Colaboradores/{header?.local?.name}</div>
            )}
          </div>
        </div>

        {/* Main Grid Table */}
        <table
          style={{
            width: '100%',
            borderCollapse: 'collapse',
            border: '2px solid #000',
            marginBottom: '12px',
            fontSize: '11px',
          }}
        >
          <thead>
            <tr>
              <th
                style={{
                  border: '1px solid #000',
                  padding: '6px',
                  backgroundColor: '#d9d9d9',
                  textAlign: 'left',
                }}
              >
                INSTALAÇÃO/
                <div> EQUIPAMENTO</div>
              </th>
              {Array.from({ length: 31 }).map((_, i) => (
                // eslint-disable-next-line react/no-array-index-key
                <th
                  key={`h_${i + 1}`}
                  style={{
                    border: '1px solid #000',
                    padding: '4px',
                    backgroundColor: '#d9d9d9',
                  }}
                >
                  {i + 1}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {/* Section: Limpeza Diária */}
            <tr>
              <td
                colSpan={32}
                style={{
                  border: '1px solid #000',
                  padding: '6px',
                  backgroundColor: '#d9d9d9',
                  fontWeight: 'bold',
                }}
              >
                LIMPEZA DIÁRIA
              </td>
            </tr>
            {/* Rows */}
            <tr>
              <td style={{ border: '1px solid #000', padding: '6px' }}>PISO</td>
              {renderDayCells(marks.piso)}
            </tr>
            <tr>
              <td style={{ border: '1px solid #000', padding: '6px' }}>PIA</td>
              {renderDayCells(marks.pia)}
            </tr>
            <tr>
              <td style={{ border: '1px solid #000', padding: '6px' }}>
                SIFÃO DA PIA
              </td>
              {renderDayCells(marks.sifaoPia)}
            </tr>
            <tr>
              <td style={{ border: '1px solid #000', padding: '6px' }}>MESA</td>
              {renderDayCells(marks.mesa)}
            </tr>
            <tr>
              <td style={{ border: '1px solid #000', padding: '6px' }}>
                BEBEDOURO
              </td>
              {renderDayCells(marks.bebedouros)}
            </tr>
            <tr>
              <td style={{ border: '1px solid #000', padding: '6px' }}>
                BANCOS
              </td>
              {renderDayCells(marks.bancos)}
            </tr>
            <tr>
              <td style={{ border: '1px solid #000', padding: '6px' }}>
                LIXEIRA
              </td>
              {renderDayCells(marks.lixeira)}
            </tr>
            <tr>
              <td style={{ border: '1px solid #000', padding: '6px' }}>SOFÁ</td>
              {renderDayCells(marks.sofa)}
            </tr>
            <tr>
              <td style={{ border: '1px solid #000', padding: '6px' }}>
                RETIRAR LIXO
              </td>
              {renderDayCells(marks.retirarLixo)}
            </tr>

            {/* Section: Limpeza Semanal / Conforme Necessidade */}
            <tr>
              <td
                colSpan={32}
                style={{
                  border: '1px solid #000',
                  padding: '6px',
                  backgroundColor: '#d9d9d9',
                  fontWeight: 'bold',
                }}
              >
                LIMPEZA SEMANAL / CONFORME NECESSIDADE
              </td>
            </tr>
            <tr>
              <td style={{ border: '1px solid #000', padding: '6px' }}>
                MICRO-ONDAS
              </td>
              {renderDayCells(marks.microOndas)}
            </tr>
            <tr>
              <td style={{ border: '1px solid #000', padding: '6px' }}>
                GELADEIRA
              </td>
              {renderDayCells(marks.geladeira)}
            </tr>
            <tr>
              <td style={{ border: '1px solid #000', padding: '6px' }}>
                ARMÁRIOS
              </td>
              {renderDayCells(marks.armarios)}
            </tr>
            <tr>
              <td style={{ border: '1px solid #000', padding: '6px' }}>TETO</td>
              {renderDayCells(marks.teto)}
            </tr>

            {/* Responsible signature row */}
            <tr>
              <td style={{ border: '1px solid #000', padding: '6px' }}>
                Ass. Responsável
              </td>
              {Array.from({ length: 31 }).map((_, i) => (
                // eslint-disable-next-line react/no-array-index-key
                <td
                  key={`ar_${i + 1}`}
                  style={{
                    border: '1px solid #000',
                    padding: '4px',
                    height: '18px',
                    textAlign: 'center',
                  }}
                >
                  {dayOfMonth === i + 1 ? responsavelNome : ''}
                </td>
              ))}
            </tr>
          </tbody>
        </table>

        {/* Supervision signatures */}
        <div style={{ margin: '50px 0 14px', fontSize: '11px' }}>
          <div style={{ display: 'flex' }}>
            <div style={{ display: 'flex', marginBottom: '8px' }}>
              Ass. Supervisão:
            </div>
            <div
              style={{
                display: 'flex',
                flex: 1,
                justifyContent: 'space-between',
                padding: '0 40px',
              }}
            >
              <div>__________________________</div>
              <div>__________________________</div>
              <div>__________________________</div>
              <div>__________________________</div>
            </div>
          </div>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              padding: '0 40px',
              marginLeft: '130px',
              marginRight: '50px',
            }}
          >
            <div>Semana 1</div>
            <div>Semana 2</div>
            <div>Semana 3</div>
            <div>Semana 4</div>
          </div>
        </div>

        {/* Footer Table */}
        <table
          style={{
            width: '100%',
            borderCollapse: 'collapse',
            border: '2px solid #000',
            fontSize: '11px',
          }}
        >
          <tbody>
            <tr>
              <td
                style={{
                  border: '1px solid #000',
                  padding: '8px',
                  width: '33%',
                }}
              >
                <strong>Elaboração</strong>
                <div>
                  <strong>Elaborado por:</strong> {elaboradoPor}
                </div>
                <div>
                  <strong>Data de elaboração:</strong> {dataElaboracao}
                </div>
              </td>
              <td
                style={{
                  border: '1px solid #000',
                  padding: '8px',
                  width: '33%',
                }}
              >
                <strong>Aprovação</strong>
                <div>
                  <strong>Aprovado por:</strong> {aprovadoPor}
                </div>
                <div>
                  <strong>Data de aprovação:</strong> {date}
                </div>
              </td>
              <td
                style={{
                  border: '1px solid #000',
                  padding: '8px',
                  width: '34%',
                }}
              >
                <strong>Revisão</strong>
                <div>
                  <strong>Código:</strong> 001
                </div>
                <div>
                  <strong>Data da revisão:</strong> {dataRevisao}
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </ReportContext>
  );
}
